import {
  MODULES,
  SYNERGIES,
  evaluateSynergies,
  getModule,
  type ModuleDefinition,
  type SynergyDefinition,
  type SynergyRequirement,
} from "../../../../packages/game-core/src";

export interface OfferSynergyRecipe {
  synergyId: string;
  synergyName: string;
  sourceModuleId: string;
  requiresAscension: boolean;
  moduleIds: string[];
  formula: string;
}

const offerRecipeCache = new Map<string, OfferSynergyRecipe[]>();

export function createOfferSynergyRecipes(
  moduleId: string,
  preferredModuleIds: readonly string[] = [],
): OfferSynergyRecipe[] {
  const preferenceKey = [...new Set(preferredModuleIds)].sort().join("|");
  const cacheKey = `${moduleId}::${preferenceKey}`;
  const cached = offerRecipeCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const candidate = getModule(moduleId);
  const ascendedModuleId =
    candidate.tier === 5
      ? candidate.stagedAscension?.targetModuleId ?? candidate.evolutionTo
      : undefined;
  const sources = [
    { module: candidate, requiresAscension: false },
    ...(ascendedModuleId
      ? [{ module: getModule(ascendedModuleId), requiresAscension: true }]
      : []),
  ];
  const recipes = sources
    .flatMap(({ module: source, requiresAscension }) => {
      const preferred = new Set(
        preferredModuleIds
          .filter((id) => id !== moduleId)
          .concat(source.id),
      );
      return SYNERGIES.filter((synergy) =>
        synergyCanUseModule(synergy, source),
      ).flatMap((synergy) => {
        const moduleIds = buildConcreteRecipe(source.id, synergy, preferred);
        if (
          moduleIds.length === 0 ||
          !evaluateSynergies(moduleIds).some(
            (active) => active.id === synergy.id,
          )
        ) {
          return [];
        }
        return [
          {
            synergyId: synergy.id,
            synergyName: synergy.name,
            sourceModuleId: source.id,
            requiresAscension,
            moduleIds,
            formula:
              (requiresAscension ? `升格为${source.name}后：` : "") +
              `${moduleIds.map((id) => getModule(id).name).join(" + ")}` +
              ` = ${synergy.name}`,
          },
        ];
      });
    })
    .filter(
      (recipe, index, all) =>
        all.findIndex(
          (candidateRecipe) =>
            candidateRecipe.synergyId === recipe.synergyId &&
            candidateRecipe.sourceModuleId === recipe.sourceModuleId,
        ) === index,
    )
    .sort(
      (a, b) =>
        Number(a.requiresAscension) - Number(b.requiresAscension) ||
        a.moduleIds.length - b.moduleIds.length ||
        a.synergyName.localeCompare(b.synergyName, "zh-CN"),
    );
  offerRecipeCache.set(cacheKey, recipes);
  return recipes;
}

function synergyCanUseModule(
  synergy: SynergyDefinition,
  module: ModuleDefinition,
): boolean {
  return synergy.requirements.some((requirement) => {
    switch (requirement.kind) {
      case "module":
        return requirement.moduleId === module.id;
      case "synergy": {
        const dependency = SYNERGIES.find(
          (candidate) => candidate.id === requirement.synergyId,
        );
        return dependency ? synergyCanUseModule(dependency, module) : false;
      }
      case "one_of":
        return requirement.moduleIds.includes(module.id);
      case "tag_count":
        return module.tags.includes(requirement.tag);
      case "tier_count":
        return module.tier >= requirement.minTier;
    }
  });
}

function buildConcreteRecipe(
  moduleId: string,
  synergy: SynergyDefinition,
  preferred: ReadonlySet<string>,
): string[] {
  const selected = new Set<string>([moduleId]);

  for (const requirement of synergy.requirements) {
    if (requirement.kind === "module") {
      selected.add(requirement.moduleId);
    } else if (requirement.kind === "synergy") {
      const dependency = SYNERGIES.find(
        (candidate) => candidate.id === requirement.synergyId,
      );
      if (!dependency) {
        continue;
      }
      const seed =
        MODULES.find(
          (module) =>
            selected.has(module.id) &&
            synergyCanUseModule(dependency, module),
        ) ??
        MODULES.filter((module) => synergyCanUseModule(dependency, module))
          .sort((a, b) =>
            compareRecipeCandidates(
              a,
              b,
              preferred,
              dependency,
              selected,
            ),
          )[0];
      if (!seed) {
        continue;
      }
      for (const dependencyModuleId of buildConcreteRecipe(
        seed.id,
        dependency,
        preferred,
      )) {
        selected.add(dependencyModuleId);
      }
    }
  }

  for (const requirement of synergy.requirements) {
    if (
      requirement.kind !== "one_of" ||
      requirementMet(requirement, selected)
    ) {
      continue;
    }
    const choice = [...requirement.moduleIds]
      .map((id) => getModule(id))
      .sort((a, b) =>
        compareRecipeCandidates(a, b, preferred, synergy, selected),
      )[0];
    if (choice) {
      selected.add(choice.id);
    }
  }

  while (
    !synergy.requirements.every((requirement) =>
      requirementMet(requirement, selected),
    ) &&
    selected.size < 6
  ) {
    const next = MODULES.filter((module) => !selected.has(module.id))
      .map((module) => ({
        module,
        coverage: unmetCoverage(module, synergy, selected),
      }))
      .filter((entry) => entry.coverage > 0)
      .sort(
        (a, b) =>
          b.coverage - a.coverage ||
          compareRecipeCandidates(
            a.module,
            b.module,
            preferred,
            synergy,
            selected,
          ),
      )[0]?.module;
    if (!next) {
      break;
    }
    selected.add(next.id);
  }

  return synergy.requirements.every((requirement) =>
    requirementMet(requirement, selected),
  )
    ? [...selected]
    : [];
}

function compareRecipeCandidates(
  a: ModuleDefinition,
  b: ModuleDefinition,
  preferred: ReadonlySet<string>,
  synergy: SynergyDefinition,
  selected: ReadonlySet<string>,
): number {
  const preferredDifference =
    Number(preferred.has(b.id)) - Number(preferred.has(a.id));
  if (preferredDifference !== 0) {
    return preferredDifference;
  }
  const coverageDifference =
    unmetCoverage(b, synergy, selected) -
    unmetCoverage(a, synergy, selected);
  if (coverageDifference !== 0) {
    return coverageDifference;
  }
  const randomisableDifference =
    Number(b.weight > 0) - Number(a.weight > 0);
  if (randomisableDifference !== 0) {
    return randomisableDifference;
  }
  const hiddenDifference = Number(a.hidden) - Number(b.hidden);
  if (hiddenDifference !== 0) {
    return hiddenDifference;
  }
  return a.tier - b.tier || a.name.localeCompare(b.name, "zh-CN");
}

function unmetCoverage(
  module: ModuleDefinition,
  synergy: SynergyDefinition,
  selected: ReadonlySet<string>,
): number {
  return synergy.requirements.reduce((score, requirement) => {
    if (requirementMet(requirement, selected)) {
      return score;
    }
    switch (requirement.kind) {
      case "module":
        return score + Number(requirement.moduleId === module.id) * 4;
      case "synergy":
        return (
          score +
          Number(
            SYNERGIES.some(
              (candidate) =>
                candidate.id === requirement.synergyId &&
                synergyCanUseModule(candidate, module),
            ),
          ) *
            2
        );
      case "one_of":
        return score + Number(requirement.moduleIds.includes(module.id)) * 3;
      case "tag_count":
        return score + Number(module.tags.includes(requirement.tag));
      case "tier_count":
        return score + Number(module.tier >= requirement.minTier);
    }
  }, 0);
}

function requirementMet(
  requirement: SynergyRequirement,
  selected: ReadonlySet<string>,
): boolean {
  switch (requirement.kind) {
    case "module":
      return selected.has(requirement.moduleId);
    case "synergy":
      return evaluateSynergies([...selected]).some(
        (synergy) => synergy.id === requirement.synergyId,
      );
    case "one_of":
      return requirement.moduleIds.some((id) => selected.has(id));
    case "tag_count":
      return [...selected].filter((id) =>
        getModule(id).tags.includes(requirement.tag),
      ).length >= requirement.count;
    case "tier_count":
      return [...selected].filter(
        (id) => getModule(id).tier >= requirement.minTier,
      ).length >= requirement.count;
  }
}
